import { hasAnyTranslation } from '@assets/i18n/config';
import type { Account } from '@interfaces/account';
import type { TranslatedString } from '@interfaces/shared';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { AccountSearch } from '@ovComponents/3-pattern/accountSearch/accountSearch';
import { DraggableAccounts } from '@ovComponents/3-pattern/draggableAccounts/draggableAccounts';
import type { AccountGroupType } from '@ovComponents/4-module/widgets/editButton/pageTypes/components/editAccountGroupDialog';
import { Box, CircularProgress, IconButton, Tooltip } from 'hephaestus/web';
import { RemoveCircleOutlined as RemoveCircleOutlineIcon } from 'hephaestus/webIcons';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type AccountGroupSectionProps = {
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
  objectId?: string;
  accountGroup: AccountGroupType;
  setAccountGroup: (accountGroup: AccountGroupType) => void;
  variant: 'edit' | 'create';
  updateAccountGroup: (
    key: string,
    value: TranslatedString | Account[],
  ) => void;
  removeAccount?: (accountId: string, account: Account) => void;
  removeAccountGroup?: () => void;
  showAccountRemoveIcon?: boolean;
  showGroupRemoveIcon?: boolean;
  includeAccounts?: Account[];
  excludeAccountIds?: string[];
  showEmptyText?: boolean;
};

export const AccountGroupSection = ({
  objectType,
  objectId,
  accountGroup,
  setAccountGroup,
  variant,
  updateAccountGroup,
  removeAccount,
  removeAccountGroup,
  showAccountRemoveIcon = true,
  showGroupRemoveIcon = false,
  includeAccounts = [],
  excludeAccountIds = [],
  showEmptyText = true,
}: AccountGroupSectionProps) => {
  const { t } = useTranslation('client');
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);
  const loadMoreRef = useRef<(() => void) | null>(null);
  const searchTextRef = useRef(searchText);

  const handleSearchText = (value: string) => {
    searchTextRef.current = value;
    setSearchText(value);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          pt: '16px',
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            className='group-header-row'
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              minHeight: 40,
            }}
          >
            <TranslatableTextField
              fullWidth
              sx={{ width: '100%' }}
              label={t('accountsGroup.groupName')}
              value={accountGroup.translatedName}
              onChange={(value) => updateAccountGroup('translatedName', value)}
              error={
                variant === 'edit' &&
                !hasAnyTranslation(accountGroup.translatedName)
              }
              errorText={
                variant === 'edit' &&
                !hasAnyTranslation(accountGroup.translatedName)
                  ? t('editAccountGroup.groupNameRequired')
                  : undefined
              }
            />

            {showGroupRemoveIcon &&
              accountGroup.accounts.length > 0 &&
              hasAnyTranslation(accountGroup.translatedName) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '60%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    right: -26,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '.group-header-row:hover &': { opacity: 1 },
                  }}
                >
                  <Tooltip
                    title={t('accountsGroup.deleteGroup')}
                    placement='top'
                  >
                    <IconButton
                      aria-label='remove'
                      onClick={removeAccountGroup}
                      size='xsmall'
                    >
                      <RemoveCircleOutlineIcon
                        sx={{ width: '16px' }}
                        fontSize='inherit'
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
          </Box>
          {variant === 'edit' && (
            <AccountSearch
              label=''
              placeholder={t('accountsGroup.searchAccountGroupAccounts')}
              setAccounts={(accounts) =>
                setAccountGroup({
                  ...accountGroup,
                  accounts:
                    searchTextRef.current.length === 0 &&
                    includeAccounts.length > 0
                      ? [...accounts, ...includeAccounts]
                      : accounts,
                })
              }
              setHasMore={setHasMore}
              setLoadMoreHandler={(fn) => {
                loadMoreRef.current = fn;
              }}
              objectType={objectType}
              objectId={objectId}
              accountGroupId={accountGroup.id}
              sx={{ my: 2 }}
              page={page}
              setPage={setPage}
              searchText={searchText}
              setSearchText={handleSearchText}
              setLoading={setLoading}
              setIsSearching={setIsSearching}
              excludeAccountIds={excludeAccountIds}
              setInitialLoading={setInitialLoading}
            />
          )}
          {initialLoading ? (
            <Box
              sx={{
                height: '30vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DraggableAccounts
              droppableId={accountGroup.id}
              accounts={accountGroup.accounts}
              variant={variant}
              hasMore={hasMore}
              handleLoadMore={() => loadMoreRef.current?.()}
              onRemoveAccount={removeAccount}
              showRemoveIcon={showAccountRemoveIcon}
              objectType={objectType}
              isSearching={isSearching}
              loading={loading}
              showEmptyText={showEmptyText}
              isGroupSection
            />
          )}
        </Box>
      </Box>
    </>
  );
};
